import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { translate } from "../../../hooks/translate";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import Container from "../../../components/CustomUI/Container";
import ContainerItem from "../../../components/CustomUI/Container/ContainerItem";
import Input from "../../../components/BaseUI/Input";
import Button from "../../../components/BaseUI/Button";
import Pagination from "../../../components/BaseUI/Pagination";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiServer,
  FiSave,
  FiX,
  FiUser,
  FiCpu,
  FiSearch,
  FiMove,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import Label from "../../../components/BaseUI/Label";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const LIST_PAGINATION_OPTIONS = {
  skipPrevious: true,
  previous: true,
  next: true,
  skipNext: true,
  selectQuantity: true,
  simpleCounting: false,
};

const getUserProfileDisplayName = (user) => {
  const p = user?.profileId;
  if (p && typeof p === "object" && p.name) return p.name;
  return "—";
};

const userMatchesUserSearch = (user, search) => {
  const q = search.toLowerCase();
  const profileName =
    user.profileId && typeof user.profileId === "object" && user.profileId.name
      ? user.profileId.name.toLowerCase()
      : "";
  return (
    user.name?.toLowerCase().includes(q) ||
    user.email?.toLowerCase().includes(q) ||
    profileName.includes(q)
  );
};

const EditMachineAssignment = () => {
  useTitle("Rovion MES - Makine Atamasını Düzenle");
  const navigate = useNavigate();
  const { id } = useParams();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/machine-assignments",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [users, setUsers] = useState([]);
  const [machines, setMachines] = useState([]);
  const [activeTab, setActiveTab] = useState("users"); // users | machines
  const [userSearch, setUserSearch] = useState("");
  const [machineSearch, setMachineSearch] = useState("");
  const [dragOverUser, setDragOverUser] = useState(false);
  const [dragOverMachine, setDragOverMachine] = useState(false);
  const [assignmentData, setAssignmentData] = useState({
    assignmentName: "",
    userIds: [],
    machineIds: [],
  });
  const [userListPage, setUserListPage] = useState(1);
  const [userListLimit, setUserListLimit] = useState(10);
  const [machineListPage, setMachineListPage] = useState(1);
  const [machineListLimit, setMachineListLimit] = useState(10);
  const [selectedUserPage, setSelectedUserPage] = useState(1);
  const [selectedUserLimit, setSelectedUserLimit] = useState(10);
  const [selectedMachinePage, setSelectedMachinePage] = useState(1);
  const [selectedMachineLimit, setSelectedMachineLimit] = useState(10);

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Makine Atamaları", url: "/machine-assignments" },
    { label: "Düzenle" },
  ];

  useEffect(() => {
    fetchAssignment();
    fetchUsers();
    fetchMachines();
  }, [id]);

  const fetchAssignment = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/machine-assignments/${id}`,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );

      const assignment = response.data.assignment || response.data;
      setAssignmentData({
        assignmentName: assignment.assignmentName,
        userIds: assignment.userIds.map((u) => u._id || u),
        machineIds: assignment.machineIds.map((m) => m._id || m),
      });
    } catch (error) {
      console.error("Failed to fetch assignment:", error);
      addToast("Atama bilgileri yüklenemedi", "danger");
      navigate("/machine-assignments");
    } finally {
      setFetchingData(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/machine-assignments/assignable-users`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setUsers(response.data?.users || response.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      addToast("Kullanıcılar yüklenemedi", "danger");
    }
  };

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/machines`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setMachines(response.data?.machines || response.data || []);
    } catch (error) {
      console.error("Failed to fetch machines:", error);
      addToast("Makineler yüklenemedi", "danger");
    }
  };

  const addToast = (message, type = "info") => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts([...toasts, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  };

  const handleUserToggle = (userId) => {
    setAssignmentData((prev) => ({
      ...prev,
      userIds: prev.userIds.includes(userId)
        ? prev.userIds.filter((id) => id !== userId)
        : [...prev.userIds, userId],
    }));
  };

  const handleMachineToggle = (machineId) => {
    setAssignmentData((prev) => ({
      ...prev,
      machineIds: prev.machineIds.includes(machineId)
        ? prev.machineIds.filter((id) => id !== machineId)
        : [...prev.machineIds, machineId],
    }));
  };

  const handleSelectAllUsers = () => {
    const filteredUsers = users.filter((user) =>
      userMatchesUserSearch(user, userSearch),
    );
    const allSelected = filteredUsers.every((user) =>
      assignmentData.userIds.includes(user._id),
    );

    if (allSelected) {
      setAssignmentData((prev) => ({
        ...prev,
        userIds: prev.userIds.filter(
          (id) => !filteredUsers.some((u) => u._id === id),
        ),
      }));
    } else {
      const newUserIds = [
        ...new Set([
          ...assignmentData.userIds,
          ...filteredUsers.map((u) => u._id),
        ]),
      ];
      setAssignmentData((prev) => ({
        ...prev,
        userIds: newUserIds,
      }));
    }
  };

  const handleSelectAllMachines = () => {
    const filteredMachines = machines.filter(
      (machine) =>
        machine.name?.toLowerCase().includes(machineSearch.toLowerCase()) ||
        machine.code?.toLowerCase().includes(machineSearch.toLowerCase()),
    );
    const allSelected = filteredMachines.every((machine) =>
      assignmentData.machineIds.includes(machine._id),
    );

    if (allSelected) {
      setAssignmentData((prev) => ({
        ...prev,
        machineIds: prev.machineIds.filter(
          (id) => !filteredMachines.some((m) => m._id === id),
        ),
      }));
    } else {
      const newMachineIds = [
        ...new Set([
          ...assignmentData.machineIds,
          ...filteredMachines.map((m) => m._id),
        ]),
      ];
      setAssignmentData((prev) => ({
        ...prev,
        machineIds: newMachineIds,
      }));
    }
  };

  const handleDragStart = (e, type, id) => {
    if (e?.dataTransfer) {
      e.dataTransfer.setData("type", type);
      e.dataTransfer.setData("id", id);
      return;
    }
    if (type === "user") {
      setAssignmentData((prev) =>
        prev.userIds.includes(id)
          ? prev
          : { ...prev, userIds: [...prev.userIds, id] },
      );
    } else if (type === "machine") {
      setAssignmentData((prev) =>
        prev.machineIds.includes(id)
          ? prev
          : { ...prev, machineIds: [...prev.machineIds, id] },
      );
    }
  };

  const handleDragOver = (e, type) => {
    e.preventDefault();
    if (type === "user") {
      setDragOverUser(true);
    } else if (type === "machine") {
      setDragOverMachine(true);
    }
  };

  const handleDragLeave = (e, type) => {
    e.preventDefault();
    if (type === "user") {
      setDragOverUser(false);
    } else if (type === "machine") {
      setDragOverMachine(false);
    }
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    const draggedType = e.dataTransfer.getData("type");
    const draggedId = e.dataTransfer.getData("id");

    if (type === "user") {
      setDragOverUser(false);
    } else if (type === "machine") {
      setDragOverMachine(false);
    }

    if (draggedType === type) {
      if (type === "user") {
        if (!assignmentData.userIds.includes(draggedId)) {
          setAssignmentData((prev) => ({
            ...prev,
            userIds: [...prev.userIds, draggedId],
          }));
        }
      } else if (type === "machine") {
        if (!assignmentData.machineIds.includes(draggedId)) {
          setAssignmentData((prev) => ({
            ...prev,
            machineIds: [...prev.machineIds, draggedId],
          }));
        }
      }
    }
  };

  const removeUser = (userId) => {
    setAssignmentData((prev) => ({
      ...prev,
      userIds: prev.userIds.filter((id) => id !== userId),
    }));
  };

  const removeMachine = (machineId) => {
    setAssignmentData((prev) => ({
      ...prev,
      machineIds: prev.machineIds.filter((id) => id !== machineId),
    }));
  };

  const filteredUsers = users.filter((user) =>
    userMatchesUserSearch(user, userSearch),
  );

  const filteredMachines = machines.filter(
    (machine) =>
      machine.name?.toLowerCase().includes(machineSearch.toLowerCase()) ||
      machine.code?.toLowerCase().includes(machineSearch.toLowerCase()),
  );

  const selectedUsers = users.filter((user) =>
    assignmentData.userIds.includes(user._id),
  );

  const selectedMachines = machines.filter((machine) =>
    assignmentData.machineIds.includes(machine._id),
  );

  const leftPanelUsers = filteredUsers.filter(
    (user) => !assignmentData.userIds.includes(user._id),
  );
  const leftPanelMachines = filteredMachines.filter(
    (machine) => !assignmentData.machineIds.includes(machine._id),
  );

  const paginatedUsers = leftPanelUsers.slice(
    (userListPage - 1) * userListLimit,
    userListPage * userListLimit,
  );
  const paginatedMachines = leftPanelMachines.slice(
    (machineListPage - 1) * machineListLimit,
    machineListPage * machineListLimit,
  );
  const paginatedSelectedUsers = selectedUsers.slice(
    (selectedUserPage - 1) * selectedUserLimit,
    selectedUserPage * selectedUserLimit,
  );
  const paginatedSelectedMachines = selectedMachines.slice(
    (selectedMachinePage - 1) * selectedMachineLimit,
    selectedMachinePage * selectedMachineLimit,
  );

  useEffect(() => {
    setUserListPage(1);
  }, [userSearch]);

  useEffect(() => {
    setMachineListPage(1);
  }, [machineSearch]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(leftPanelUsers.length / userListLimit),
    );
    if (userListPage > maxPage) setUserListPage(maxPage);
  }, [leftPanelUsers.length, userListLimit, userListPage]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(leftPanelMachines.length / machineListLimit),
    );
    if (machineListPage > maxPage) setMachineListPage(maxPage);
  }, [leftPanelMachines.length, machineListLimit, machineListPage]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(selectedUsers.length / selectedUserLimit),
    );
    if (selectedUserPage > maxPage) setSelectedUserPage(maxPage);
  }, [selectedUsers.length, selectedUserLimit, selectedUserPage]);

  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(selectedMachines.length / selectedMachineLimit),
    );
    if (selectedMachinePage > maxPage) setSelectedMachinePage(maxPage);
  }, [selectedMachines.length, selectedMachineLimit, selectedMachinePage]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === "users") {
      setUserListPage(1);
      setSelectedUserPage(1);
    } else {
      setMachineListPage(1);
      setSelectedMachinePage(1);
    }
  };

  const handleSubmit = async () => {
    if (!assignmentData.assignmentName.trim()) {
      addToast("Atama adı gereklidir", "warning");
      return;
    }

    if (assignmentData.userIds.length === 0) {
      addToast("En az bir kullanıcı seçilmelidir", "warning");
      return;
    }

    if (assignmentData.machineIds.length === 0) {
      addToast("En az bir makine seçilmelidir", "warning");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put(
        `${API_URL}/api/machine-assignments/${id}`,
        assignmentData,
        {
          headers: { Authorization: `Bearer ${getToken()}` },
        },
      );

      if (response.data.success) {
        addToast("Makine ataması başarıyla güncellendi", "success");
        setTimeout(() => navigate("/machine-assignments"), 1500);
      }
    } catch (error) {
      console.error("Failed to update assignment:", error);
      addToast(
        error.response?.data?.message || "Atama güncellenemedi",
        "danger",
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <AppPage
        title="Makine Atamasını Düzenle"
        subtitle="Yükleniyor..."
        icon={<FiServer />}
        breadcrumbs={breadcrumbs}
      >
        <Container>
          <ContainerItem sm={4} md={8} xl={12}>
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  if (!canWrite) {
    return (
      <AppPage title="Makine Atamasını Düzenle">
        <Container>
          <ContainerItem>
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <span>Bu sayfada değişiklik yapma yetkiniz yok.</span>
              <Button
                appearance="primary-animated"
                onClick={() => navigate("/machine-assignments")}
              >
                Geri Dön
              </Button>
            </div>
          </ContainerItem>
        </Container>
      </AppPage>
    );
  }

  return (
    <AppPage
      title="Makine Atamasını Düzenle"
      subtitle="Atama bilgilerini güncelleyin"
      icon={<FiServer />}
      breadcrumbs={breadcrumbs}
    >
      <Container>
        <ContainerItem sm={4} md={8} xl={12}>
          <div className="card shadow-sm">
            <div className="card-body">
              <div className="section-container">
                <div className="section-header">
                  <div className="section-icon">
                    <FiServer className="text-primary" />
                  </div>
                  <div className="section-title">
                    <h5 className="mb-1">Atama Bilgileri</h5>
                    <p className="text-muted small mb-0">
                      Atama detaylarını güncelleyin
                    </p>
                  </div>
                </div>

                <div className="section-content">
                  <div className="row g-3">
                    <div className="col-12">
                      <Label required>Atama Adı</Label>
                      <Input
                        type="text"
                        placeholder="Örn: Sabah Vardiyası"
                        value={assignmentData.assignmentName}
                        onChange={(value) =>
                          setAssignmentData({
                            ...assignmentData,
                            assignmentName: value,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="selection-section">
                    <div className="selection-tabs">
                      <button
                        type="button"
                        className={`selection-tab-btn ${
                          activeTab === "users" ? "active" : ""
                        }`}
                        onClick={() => switchTab("users")}
                      >
                        <FiUser />
                        Personel
                      </button>
                      <button
                        type="button"
                        className={`selection-tab-btn ${
                          activeTab === "machines" ? "active" : ""
                        }`}
                        onClick={() => switchTab("machines")}
                      >
                        <FiCpu />
                        Makine
                      </button>
                    </div>
                    <div className="selection-left-panel">
                      {activeTab === "users" && (
                        <div className="personnel-section">
                          <div className="section-title-header">
                            <FiUser />
                            <span>Personel Seçimi</span>
                          </div>
                          <div className="search-container">
                            <FiSearch className="search-icon" />
                            <Input
                              type="text"
                              placeholder="Personel ara..."
                              value={userSearch}
                              onChange={(value) => setUserSearch(value)}
                              className="search-input"
                            />
                          </div>
                          <div className="selection-info-bar">
                            <span>
                              {leftPanelUsers.length} personel
                              {/* {filteredUsers.length !== leftPanelUsers.length
                                ? ` (${filteredUsers.length} aramada)`
                                : ""} */}
                            </span>
                            <Button
                              appearance="ghost"
                              size="sm"
                              onClick={handleSelectAllUsers}
                            >
                              Tümünü Seç
                            </Button>
                          </div>
                          <div className="selection-list">
                            {paginatedUsers.map((user) => (
                              <div
                                key={user._id}
                                className="selection-item"
                                draggable
                                onDragStart={(e) =>
                                  handleDragStart(e, "user", user._id)
                                }
                              >
                                <FiUser className="item-icon" />
                                <div className="item-info">
                                  <strong>{user.name}</strong>
                                  <small>
                                    {getUserProfileDisplayName(user)}
                                  </small>
                                </div>
                                <div className="item-actions">
                                  <Button
                                    appearance="warning-subtle"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDragStart(e, "user", user._id);
                                    }}
                                    icon={<FiMove />}
                                  >
                                    Sürükle
                                  </Button>
                                  <Button
                                    appearance="primary-subtle"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUserToggle(user._id);
                                    }}
                                    icon={<FiCheck />}
                                  >
                                    Seç
                                  </Button>
                                </div>
                              </div>
                            ))}
                            {leftPanelUsers.length === 0 && (
                              <div className="empty-message">
                                {filteredUsers.length === 0
                                  ? "Personel bulunamadı"
                                  : "Bu aramada seçilecek personel kalmadı"}
                              </div>
                            )}
                          </div>
                          {leftPanelUsers.length > 0 && (
                            <div className="mt-2">
                              <Pagination
                                totalItems={leftPanelUsers.length}
                                totalRecords={leftPanelUsers.length}
                                totalPerPage={userListLimit}
                                currentPage={userListPage}
                                options={LIST_PAGINATION_OPTIONS}
                                onPageChange={({ pagination: p }) => {
                                  setUserListLimit(p.itemsPerPage);
                                  setUserListPage(p.currentPage);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {activeTab === "machines" && (
                        <div className="device-section">
                          <div className="section-title-header">
                            <FiCpu />
                            <span>Makine Seçimi</span>
                          </div>
                          <div className="search-container">
                            <FiSearch className="search-icon" />
                            <Input
                              type="text"
                              placeholder="Makine ara..."
                              value={machineSearch}
                              onChange={(value) => setMachineSearch(value)}
                              className="search-input"
                            />
                          </div>
                          <div className="selection-info-bar">
                            <span>
                              {leftPanelMachines.length} makine
                              {/* {filteredMachines.length !==
                              leftPanelMachines.length
                                ? ` (${filteredMachines.length} aramada)`
                                : ""} */}
                            </span>
                            <Button
                              appearance="ghost"
                              size="sm"
                              onClick={handleSelectAllMachines}
                            >
                              Tümünü Seç
                            </Button>
                          </div>
                          <div className="selection-list">
                            {paginatedMachines.map((machine) => (
                              <div
                                key={machine._id}
                                className="selection-item"
                                draggable
                                onDragStart={(e) =>
                                  handleDragStart(e, "machine", machine._id)
                                }
                              >
                                <FiCpu className="item-icon" />
                                <div className="item-info">
                                  <strong>{machine.name}</strong>
                                  <small>{machine.code}</small>
                                </div>
                                <div className="item-actions">
                                  <Button
                                    appearance="warning-subtle"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDragStart(
                                        e,
                                        "machine",
                                        machine._id,
                                      );
                                    }}
                                    icon={<FiMove />}
                                  >
                                    Sürükle
                                  </Button>
                                  <Button
                                    appearance="primary-subtle"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMachineToggle(machine._id);
                                    }}
                                    icon={<FiCheck />}
                                  >
                                    Seç
                                  </Button>
                                </div>
                              </div>
                            ))}
                            {leftPanelMachines.length === 0 && (
                              <div className="empty-message">
                                {filteredMachines.length === 0
                                  ? "Makine bulunamadı"
                                  : "Bu aramada seçilecek makine kalmadı"}
                              </div>
                            )}
                          </div>
                          {leftPanelMachines.length > 0 && (
                            <div className="mt-2">
                              <Pagination
                                totalItems={leftPanelMachines.length}
                                totalRecords={leftPanelMachines.length}
                                totalPerPage={machineListLimit}
                                currentPage={machineListPage}
                                options={LIST_PAGINATION_OPTIONS}
                                onPageChange={({ pagination: p }) => {
                                  setMachineListLimit(p.itemsPerPage);
                                  setMachineListPage(p.currentPage);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="selection-right-panel">
                      {activeTab === "users" && (
                        <div className="selected-personnel-section">
                          <div className="selected-section-header">
                            <FiCheck className="check-icon" />
                            <span>
                              Seçilen Personeller ({selectedUsers.length})
                            </span>
                          </div>
                          <div
                            className={`drop-zone ${
                              dragOverUser ? "drag-over" : ""
                            }`}
                            onDragOver={(e) => handleDragOver(e, "user")}
                            onDragLeave={(e) => handleDragLeave(e, "user")}
                            onDrop={(e) => handleDrop(e, "user")}
                          >
                            {selectedUsers.length === 0 ? (
                              <div className="drop-zone-empty">
                                <FiUser className="drop-zone-icon" />
                                <p>Personel sürükleyin veya seçin</p>
                              </div>
                            ) : (
                              <>
                                <div className="selected-items-list">
                                  {paginatedSelectedUsers.map((user) => (
                                    <div
                                      key={user._id}
                                      className="selected-item"
                                    >
                                      <FiUser className="selected-item-icon" />
                                      <div className="selected-item-info">
                                        <strong>{user.name}</strong>
                                        <small>
                                          {getUserProfileDisplayName(user)}
                                        </small>
                                      </div>
                                      <Button
                                        appearance="subtle"
                                        onClick={() => removeUser(user._id)}
                                      >
                                        <FiX size={16} />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                                {selectedUsers.length > 0 && (
                                  <div className="mt-2">
                                    <Pagination
                                      totalItems={selectedUsers.length}
                                      totalRecords={selectedUsers.length}
                                      totalPerPage={selectedUserLimit}
                                      currentPage={selectedUserPage}
                                      options={LIST_PAGINATION_OPTIONS}
                                      onPageChange={({ pagination: p }) => {
                                        setSelectedUserLimit(p.itemsPerPage);
                                        setSelectedUserPage(p.currentPage);
                                      }}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {activeTab === "machines" && (
                        <div className="selected-device-section">
                          <div className="selected-section-header">
                            <FiCheck className="check-icon" />
                            <span>
                              Seçilen Makineler ({selectedMachines.length})
                            </span>
                          </div>
                          <div
                            className={`drop-zone ${
                              dragOverMachine ? "drag-over" : ""
                            }`}
                            onDragOver={(e) => handleDragOver(e, "machine")}
                            onDragLeave={(e) => handleDragLeave(e, "machine")}
                            onDrop={(e) => handleDrop(e, "machine")}
                          >
                            {selectedMachines.length === 0 ? (
                              <div className="drop-zone-empty">
                                <FiCpu className="drop-zone-icon" />
                                <p>Makine sürükleyin veya seçin</p>
                              </div>
                            ) : (
                              <>
                                <div className="selected-items-list">
                                  {paginatedSelectedMachines.map((machine) => (
                                    <div
                                      key={machine._id}
                                      className="selected-item"
                                    >
                                      <FiCpu className="selected-item-icon" />
                                      <div className="selected-item-info">
                                        <strong>{machine.name}</strong>
                                        <small>{machine.code}</small>
                                      </div>
                                      <Button
                                        appearance="subtle"
                                        onClick={() =>
                                          removeMachine(machine._id)
                                        }
                                      >
                                        <FiX size={16} />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                                {selectedMachines.length > 0 && (
                                  <div className="mt-2">
                                    <Pagination
                                      totalItems={selectedMachines.length}
                                      totalRecords={selectedMachines.length}
                                      totalPerPage={selectedMachineLimit}
                                      currentPage={selectedMachinePage}
                                      options={LIST_PAGINATION_OPTIONS}
                                      onPageChange={({ pagination: p }) => {
                                        setSelectedMachineLimit(p.itemsPerPage);
                                        setSelectedMachinePage(p.currentPage);
                                      }}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-actions p-3">
                <Button
                  appearance="danger-subtle"
                  onClick={() => navigate("/machine-assignments")}
                  icon={<FiX />}
                >
                  İptal
                </Button>
                <Button
                  appearance="primary-animated"
                  onClick={handleSubmit}
                  loading={loading}
                  icon={<FiSave />}
                >
                  Güncelle
                </Button>
              </div>
            </div>
          </div>
        </ContainerItem>
      </Container>

      <style>
        {`
          .section-container {
            margin-bottom: 2rem;
            padding: 1.5rem;
            background: #fff;
            border-radius: 8px;
            border: 1px solid #e9ecef;
          }

          .section-header {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            margin-bottom: 1.5rem;
          }

          .section-icon {
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--bs-primary-bg-subtle);
            border-radius: 8px;
          }

          .section-icon svg {
            width: 24px;
            height: 24px;
          }

          .section-title h5 {
            font-size: 1.1rem;
            margin: 0;
            color: var(--bs-gray-900);
          }

          .selection-section {
            display: grid;
            grid-template-rows: auto 1fr;
            grid-template-columns: 1fr 1fr;
            gap: 1.5rem;
            margin-top: 1.5rem;
            height: 65vh;
            min-height: 720px;
            align-items: stretch;
          }

          .selection-tabs {
            grid-column: 1 / -1;
            display: flex;
            gap: 28px;
            padding: 0 2px;
            border-bottom: 1px solid #e9ecef;
          }

          .selection-tab-btn {
            border: 0;
            background: transparent;
            border-radius: 0;
            padding: 10px 2px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            font-weight: 600;
            font-size: 14px;
            color: #6c757d;
            transition: color 0.15s ease, border-color 0.15s ease;
            border-bottom: 2px solid transparent;
            margin-bottom: -1px;
          }

          .selection-tab-btn svg {
            width: 18px;
            height: 18px;
          }

          .selection-tab-btn:hover {
            color: var(--m-primary);
            cursor: pointer;
          }

          .selection-tab-btn:hover svg {
            color: var(--m-primary);
          }

          .selection-tab-btn.active {
            color: var(--m-primary);
            border-bottom-color: var(--m-primary);
            background: var(--m-background-subtle-primary, transparent);
            border-radius: 6px 6px 0 0;
            padding: 5px 10px;
          }

          .selection-tab-btn.active svg {
            color: var(--m-primary);
          }

          .selection-left-panel,
          .selection-right-panel {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            min-height: 0;
            overflow: hidden;
          }

          .personnel-section,
          .device-section {
            border: 1px solid #e2e5e8;
            border-radius: 8px;
            background: #fff;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            flex: 1;
            min-height: 0;
          }

          .section-title-header {
            padding: 12px 16px;
            background: #f8f9fa;
            border-bottom: 1px solid #e2e5e8;
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: 600;
            color: #495057;
            font-size: 14px;
          }

          .section-title-header svg {
            width: 18px;
            height: 18px;
          }

          .search-container {
            position: relative;
            padding: 12px 16px;
            border-bottom: 1px solid #e2e5e8;
          }

          .search-icon {
            position: absolute;
            left: 28px;
            top: 50%;
            transform: translateY(-50%);
            color: #6c757d;
            width: 18px;
            height: 18px;
            pointer-events: none;
            z-index: 1;
          }

          .search-input input {
            padding-left: 40px !important;
          }

          .selection-info-bar {
            padding: 10px 16px;
            background: #f8f9fa;
            border-bottom: 1px solid #e2e5e8;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 13px;
            color: #495057;
          }

          .selection-list {
            flex: 1;
            min-height: 0;
            overflow-y: auto;
          }

          .selection-item {
            padding: 10px 12px;
            border-bottom: 1px solid #f1f3f5;
            display: flex;
            align-items: center;
            gap: 12px;
            cursor: grab;
            transition: all 0.2s;
          }

          .selection-item:active {
            cursor: grabbing;
          }

          .selection-item:hover {
            background: #f8f9fa;
          }

          .item-icon {
            width: 20px;
            height: 20px;
            color: #6c757d;
            flex-shrink: 0;
          }

          .item-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .item-info strong {
            font-size: 14px;
            color: #212529;
          }

          .item-info small {
            font-size: 12px;
            color: #6c757d;
          }

          .item-actions {
            display: flex;
            gap: 6px;
          }

          .selected-personnel-section,
          .selected-device-section {
            border: 1px solid #e2e5e8;
            border-radius: 8px;
            background: #fff;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            flex: 1;
            min-height: 0;
          }

          .selected-section-header {
            padding: 12px 16px;
            background: #f8f9fa;
            border-bottom: 1px solid #e2e5e8;
            display: flex;
            align-items: center;
            gap: 8px;
            font-weight: 600;
            color: #495057;
            font-size: 14px;
          }

          .check-icon {
            width: 18px;
            height: 18px;
            color: #28a745;
          }

          .drop-zone {
            flex: 1;
            min-height: 0;
            padding: 16px;
            border: 2px dashed #a0e7b3;
            border-radius: 8px;
            margin: 16px;
            background: #f0fdf4;
            transition: all 0.2s;
            overflow-y: auto;
          }

          .drop-zone.drag-over {
            border-color: #10b981;
            background: #d1fae5;
          }

          .drop-zone-empty {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 150px;
            color: #6c757d;
          }

          .drop-zone-icon {
            width: 64px;
            height: 64px;
            color: #a0e7b3;
            margin-bottom: 12px;
          }

          .drop-zone-empty p {
            margin: 0;
            font-size: 14px;
            color: #6c757d;
          }

          .selected-items-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .selected-item {
            padding: 10px 12px;
            background: #fff;
            border: 1px solid #e2e5e8;
            border-radius: 6px;
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .selected-item-icon {
            width: 18px;
            height: 18px;
            color: #10b981;
            flex-shrink: 0;
          }

          .selected-item-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .selected-item-info strong {
            font-size: 13px;
            color: #212529;
          }

          .selected-item-info small {
            font-size: 11px;
            color: #6c757d;
          }

          .empty-message {
            padding: 32px;
            text-align: center;
            color: #6c757d;
            font-size: 14px;
          }

          .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
            margin-top: 2rem;
            padding-top: 1rem;
            border-top: 1px solid #e9ecef;
          }

          @media (max-width: 1200px) {
            .selection-section {
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 768px) {
            .section-container {
              padding: 1rem;
            }

            .item-actions {
              flex-direction: column;
            }
          }
        `}
      </style>

      <ToastContainer>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onClose={() =>
              setToasts((toasts) => toasts.filter((t) => t.id !== toast.id))
            }
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default EditMachineAssignment;
